import { useCallback } from 'react';
import moment from 'moment-timezone';

import useRequestJSON, { RunnerSegment } from '../hooks/useRequestJSON';
import { BaseResponse, PostJSON } from '../request';
import { ISession, SingleCommunication } from '../session';
import { ISchedule } from '../schedule';

export interface IEpxSurgerySchedule extends ISchedule {
  surgeryDate: Date;
  modules: Record<
    string,
    {
      key: string;
      start: string;
      end: string;
      order: number;
      step: number;
      isActive: boolean;
      meta?: any;
    }
  >;
  presetId?: number;
  configuration?: string;
  alertStep?: number;
  alertThreshold?: number;
}

export function ProcessSchedule<
  T extends {
    surgeryTime: string;
  }
>(data: T, schedule: IEpxSurgerySchedule): T {
  const start = moment.tz(schedule.start, schedule.tz);
  const surgeryDate = moment.tz(schedule.surgeryDate, schedule.tz);
  surgeryDate.hour(start.hour()).minute(start.minute()).startOf('minute');
  data.surgeryTime = surgeryDate.format();
  return data;
}

export enum MissedReason {
  PAIN = 1,
  NO_TIME,
  FORGOT,
  OTHER
}

export interface ModulePart {
  key: string;
  sessionId: number;
  isComplete: boolean;
}

export interface IEpxSurgery
  extends ISession<IEpxSurgerySchedule>, SingleCommunication {
  parts?: Array<ModulePart>;
  _decol?: IEpxSurgeryDecol;
  _pain?: IEpxSurgeryPain;
  _wound?: IEpxSurgeryWound;
  _fever?: IEpxSurgeryFever;
  _pt?: IEpxSurgeryPT;
  _walking?: IEpxSurgeryWalking;
  _medication?: IEpxSurgeryMedication;
  _spirometer?: IEpxSurgerySpirometer;
  _appointments?: IEpxSurgeryAppointment;
  _breath?: IEpxSurgeryBreath;
  _pedsOsat?: IEpxSurgeryPedsOSat;
  _pedsWeight?: IEpxSurgeryPedsWeight;
  _pedsHr?: IEpxSurgeryPedsHr;
  _pedsSvh?: IEpxSurgeryPedsSvh;
  _sobCardiac?: IEpxSurgerySobCardiac;
  _drainage?: IEpxSurgeryDrainage;
  _weightGain?: IEpxSurgeryWeightGain;
  _medAdherence?: IEpxSurgeryMedAdherence;
  _homeNurse?: IEpxSurgeryHomeNurse;
}

export interface IEpxSurgeryDecol extends ISession {
  isSupplyCheck?: boolean;
  hasSupplies?: boolean;
  usedSupplies?: boolean;
}

export interface IEpxSurgeryPain extends ISession {
  level?: number;
  trend?: number;
}

export interface IEpxSurgeryWound extends ISession {
  degeneration?: boolean;
}

export interface IEpxSurgeryFever extends ISession {
  fever?: boolean;
  temperature?: number;
}

export interface IEpxSurgeryPT extends ISession {
  isRecievedCheck?: boolean;
  hasExercises?: boolean;
  didExercises?: boolean;
  missedReason?:
    | MissedReason.PAIN
    | MissedReason.NO_TIME
    | MissedReason.FORGOT
    | MissedReason.OTHER;
}

export interface IEpxSurgeryWalking extends ISession {
  didWalk?: boolean;
}

export interface IEpxSurgeryMedication extends ISession {
  hasMedication?: boolean;
}

export interface IEpxSurgerySpirometer extends ISession {
  isSupplyCheck?: boolean;
  hasSpirometer?: boolean;
  usedSpirometer?: boolean;
}

export interface IEpxSurgeryAppointment extends ISession {
  hasScheduled?: boolean;
}

export interface IEpxSurgeryBreath extends ISession {
  hasSymptoms?: boolean;
}

export interface IEpxSurgeryPedsOSat extends ISession {
  saturation?: number;
}

export interface IEpxSurgeryPedsWeight extends ISession {
  weight?: number;
}

export interface IEpxSurgeryPedsHr extends ISession {
  heartRate?: number;
}

export interface IEpxSurgeryPedsSvh extends ISession {
  symptomsList?: string[];
}

export interface IEpxSurgerySobCardiac extends ISession {
  hasSymptoms?: boolean;
}

export interface IEpxSurgeryDrainage extends ISession {
  hasDrainage?: boolean;
}

export interface IEpxSurgeryWeightGain extends ISession {
  gainedWeight?: boolean;
}

export interface IEpxSurgeryMedAdherence extends ISession {
  isTakingMeds?: boolean;
}

export interface IEpxSurgeryHomeNurse extends ISession {
  nurseHasContacted?: boolean;
}

// Presets

export interface EpxSurgeryModuleConfig {
  name: string;
  type: 'pre' | 'post';
  start: number;
  end: number;
  order: number;
  meta?: any;
}

interface GetModuleConfigsResponse extends BaseResponse {
  modules: Record<string, EpxSurgeryModuleConfig>;
}

export const useGetModuleConfigs = (): GetModuleConfigsResponse['modules'] => {
  const url = '/epxsurgery/data_modules/';
  const request = useCallback(
    () =>
      PostJSON({
        url,
        params: {}
      }),
    []
  );
  const { data } = useRequestJSON<
    GetModuleConfigsResponse,
    GetModuleConfigsResponse
  >({
    request
  });
  return data ? data.modules : null;
};

export interface EpxSurgeryPresetModule {
  key?: string;
  start: number;
  end?: number;
  step?: number;
  isActive?: boolean;
}

export interface IEpxSurgeryPreset {
  id?: number;
  name: string;
  modules: {
    [module: string]: EpxSurgeryPresetModule;
  };
  alertStep?: number;
  alertThreshold?: number;
  isDeleted?: boolean;
}

export interface IEpxSurgerySystemPreset {
  name: string;
  modules: {
    [module: string]: EpxSurgeryPresetModule;
  };
  alerts: {
    step: number;
    threshold: number;
  };
}

interface GetSystemPresetsResponse extends BaseResponse {
  presets: Record<string, IEpxSurgerySystemPreset>;
}

interface GetAccountPresetsResponse extends BaseResponse {
  presets: IEpxSurgeryPreset[];
}

export const useGetSystemPresets = (): GetSystemPresetsResponse['presets'] => {
  const url = '/epxsurgery/data_configurations/';
  const request = useCallback(
    () =>
      PostJSON({
        url,
        params: {}
      }),
    []
  );
  const { data } = useRequestJSON<GetSystemPresetsResponse>({
    request
  });
  return data ? data.presets : null;
};

export const useGetAccountPresets = (): Omit<
  GetAccountPresetsResponse,
  'status'
> &
  RunnerSegment => {
  const url = '/epxsurgery/data_presets/';
  const request = useCallback(
    () =>
      PostJSON({
        url,
        params: {}
      }),
    []
  );
  const { data, run } = useRequestJSON<GetAccountPresetsResponse>({
    request
  });
  return {
    presets: data ? data.presets : null,
    run: () => run(request)
  };
};
